import { t } from '@avalara/bat-apps-shared/i18n';
import { getFormFieldValidations, isEmailValid } from '@avalara/bat-apps-shared/lib/form-validation';
import { type FormEvent, useRef, useState } from 'react';

import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { submitAviatorCrushSignup } from '../../lib/side-effects/aviator-crush';
import { FieldErrorMessage, FormInput } from '../styled/components/sign-up';
import { FormNavButtons } from '../styled/skylab/page';

type FieldName = 'firstName' | 'lastName' | 'email';

type FormState = Record<FieldName, string>;
type TouchedState = Record<FieldName, boolean>;

const INITIAL_FORM_STATE: FormState = { firstName: '', lastName: '', email: '' };
const INITIAL_TOUCHED_STATE: TouchedState = { firstName: false, lastName: false, email: false };

const { firstName: firstNamePattern, lastName: lastNamePattern } = getFormFieldValidations(null);

const isFieldValid = (fieldName: FieldName, value: string): boolean => {
  if (fieldName === 'email') return isEmailValid(value);
  const pattern = fieldName === 'firstName' ? firstNamePattern : lastNamePattern;
  return value.trim().length > 0 && pattern.test(value);
};

const isFormValid = (form: FormState): boolean =>
  (Object.keys(form) as Array<FieldName>).every((fieldName) => isFieldValid(fieldName, form[fieldName]));

const LeadForm = () => {
  const dispatch = useAppDispatch();
  const [form, setForm] = useState<FormState>(INITIAL_FORM_STATE);
  const [touched, setTouched] = useState<TouchedState>(INITIAL_TOUCHED_STATE);
  const [submitting, setSubmitting] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);

  // Guards against rapid repeat taps firing duplicate requests before the `submitting`
  // state update has re-rendered the disabled button (AC: double-submit protection).
  const submissionInFlight = useRef(false);

  const handleFieldChange = (fieldName: FieldName) => (event: FormEvent<HTMLInputElement>) => {
    // Read the value synchronously - `event.currentTarget` is nullified once the handler
    // returns, but the setForm updater below may run later, after that happens.
    const value = event.currentTarget.value;
    setForm((current) => ({ ...current, [fieldName]: value }));
  };

  const handleFieldBlur = (fieldName: FieldName) => () => {
    setTouched((current) => ({ ...current, [fieldName]: true }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (submissionInFlight.current) return;

    setTouched({ firstName: true, lastName: true, email: true });
    if (!isFormValid(form)) return;

    submissionInFlight.current = true;
    setSubmitting(true);
    setSubmitFailed(false);

    try {
      const result = await submitAviatorCrushSignup(dispatch, form);

      if (result.ok) {
        window.location.href = result.value.redirectUrl;
        return;
      }
    } catch {
      // submitAviatorCrushSignup's own fetch call converts network/transport failures into a
      // Result, but getOwdBearerToken (awaited above it) can still reject outright - without
      // this, that leaves submitting/submissionInFlight stuck true forever with no way to retry.
    }

    submissionInFlight.current = false;
    setSubmitting(false);
    setSubmitFailed(true);
  };

  const errorId = (fieldName: FieldName) => `aviator-crush-${fieldName}-error`;

  // margin-all-none cancels FieldErrorMessage's own negative margin-top (a hack elsewhere
  // that overlaps the error into an input's reserved bottom padding) - with each field in
  // its own fieldset here, that hack instead shrinks the gap to the next field unevenly.
  const fieldError = (fieldName: FieldName) =>
    touched[fieldName] && !isFieldValid(fieldName, form[fieldName]) ? (
      <FieldErrorMessage id={errorId(fieldName)} className='margin-all-none pad-top-xs'>
        <p>{t(`client.aviatorCrush.errors.${fieldName}-invalid`)}</p>
      </FieldErrorMessage>
    ) : null;

  return (
    <form onSubmit={handleSubmit} noValidate>
      <fieldset className='margin-bottom-sm'>
        <label htmlFor='aviator-crush-first-name' className='required'>
          {t('client.aviatorCrush.first-name-label')}
        </label>
        <FormInput
          id='aviator-crush-first-name'
          type='text'
          value={form.firstName}
          onChange={handleFieldChange('firstName')}
          onBlur={handleFieldBlur('firstName')}
          isInvalid={touched.firstName && !isFieldValid('firstName', form.firstName)}
          aria-required='true'
          aria-invalid={touched.firstName && !isFieldValid('firstName', form.firstName)}
          aria-describedby={errorId('firstName')}
          marginAboveError=''
          disabled={submitting}
          autoComplete='given-name'
          autoFocus
        />
        {fieldError('firstName')}
      </fieldset>
      <fieldset className='margin-bottom-sm'>
        <label htmlFor='aviator-crush-last-name' className='required'>
          {t('client.aviatorCrush.last-name-label')}
        </label>
        <FormInput
          id='aviator-crush-last-name'
          type='text'
          value={form.lastName}
          onChange={handleFieldChange('lastName')}
          onBlur={handleFieldBlur('lastName')}
          isInvalid={touched.lastName && !isFieldValid('lastName', form.lastName)}
          aria-required='true'
          aria-invalid={touched.lastName && !isFieldValid('lastName', form.lastName)}
          aria-describedby={errorId('lastName')}
          marginAboveError=''
          disabled={submitting}
          autoComplete='family-name'
        />
        {fieldError('lastName')}
      </fieldset>
      <fieldset>
        <label htmlFor='aviator-crush-email' className='required'>
          {t('client.labels.email-address')}
        </label>
        <FormInput
          id='aviator-crush-email'
          type='email'
          value={form.email}
          onChange={handleFieldChange('email')}
          onBlur={handleFieldBlur('email')}
          isInvalid={touched.email && !isFieldValid('email', form.email)}
          aria-required='true'
          aria-invalid={touched.email && !isFieldValid('email', form.email)}
          aria-describedby={errorId('email')}
          marginAboveError=''
          disabled={submitting}
          autoComplete='email'
        />
        {fieldError('email')}
      </fieldset>

      {submitFailed && (
        <FieldErrorMessage data-ref='aviator-crush.lead-form.submit-error' className='margin-all-none pad-top-sm'>
          <p>
            {t('client.errors.generic-title')}. {t('client.aviatorCrush.retry-message')}
          </p>
        </FieldErrorMessage>
      )}

      <FormNavButtons>
        <button type='submit' className='primary large' disabled={submitting} data-ref='aviator-crush.lead-form.submit'>
          {submitting ? t('client.aviatorCrush.submitting') : t('client.aviatorCrush.submit-button')}
        </button>
      </FormNavButtons>
    </form>
  );
};

export default LeadForm;
